import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Languages, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale } from '@/hooks/use-locale'
import LotusMark from '@/components/generals/lotus-mark'

interface HeaderProps {
    tone?: 'light' | 'dark'
}

const Header: FC<HeaderProps> = ({ tone = 'light' }): ReactNode => {
    const router = useRouter()
    const { locale, other, t } = useLocale()
    const [open, setOpen] = useState(false)
    const dark = tone === 'dark'

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)
        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    const links = [
        { href: '/lineage', label: t.navLineage },
        { href: '/story', label: t.navStory },
        { href: '/parva', label: t.navChapters }
    ]

    const isActive = (href: string) => router.pathname === href || router.pathname.startsWith(`${href}/`)

    return (
        <header
            className={cn(
                'sticky top-0 z-40 border-b backdrop-blur-md',
                dark ? 'border-white/10 bg-night/80 text-white' : 'border-border/70 bg-background/85 text-foreground'
            )}
        >
            <a
                href='#main'
                className='sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground'
            >
                {t.skipToContent}
            </a>

            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6'>
                <Link href='/' className='group flex items-center gap-2.5 rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'>
                    <LotusMark className='size-9 transition-transform duration-500 group-hover:-translate-y-0.5' />

                    <span className='flex flex-col leading-none'>
                        <span className='font-heading text-xl font-semibold tracking-wide'>
                            {locale === 'hi' ? 'पद्मपुराण' : 'Padma Purana'}
                        </span>

                        <span className={cn('mt-1 text-[11px] uppercase tracking-[0.18em]', dark ? 'text-gold-soft/80' : 'text-muted-foreground')}>
                            {locale === 'hi' ? 'जैन रामायण' : 'पद्मपुराण · Jain Ramayana'}
                        </span>
                    </span>
                </Link>

                <nav aria-label='Primary' className='hidden items-center gap-1 md:flex'>
                    {links.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            aria-current={isActive(link.href) ? 'page' : undefined}
                            className={cn(
                                'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                                dark ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-foreground/80 hover:bg-accent hover:text-foreground',
                                isActive(link.href) && (dark ? 'bg-white/10 text-white' : 'bg-accent text-foreground')
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href={router.asPath}
                        locale={other}
                        scroll={false}
                        aria-label={t.switchLabel}
                        lang={other}
                        className={cn(
                            'inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors',
                            dark ? 'border-white/20 text-white hover:bg-white/10' : 'border-border bg-card hover:bg-accent'
                        )}
                    >
                        <Languages className='size-4' aria-hidden='true' />
                        {t.switchTo}
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? t.closeMenu : t.openMenu}
                        className={cn(
                            'inline-flex size-10 items-center justify-center rounded-full border md:hidden',
                            dark ? 'border-white/20 text-white' : 'border-border bg-card'
                        )}
                    >
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-menu'
                    aria-label='Mobile'
                    className={cn('border-t px-4 pb-5 pt-3 md:hidden', dark ? 'border-white/10' : 'border-border/70')}
                >
                    <ul className='flex flex-col gap-1'>
                        <li>
                            <Link href='/' className={cn('block rounded-lg px-3 py-3 text-base font-medium', dark ? 'hover:bg-white/10' : 'hover:bg-accent')}>
                                {t.navHome}
                            </Link>
                        </li>

                        {links.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(link.href) ? 'page' : undefined}
                                    className={cn(
                                        'block rounded-lg px-3 py-3 text-base font-medium',
                                        dark ? 'hover:bg-white/10' : 'hover:bg-accent',
                                        isActive(link.href) && (dark ? 'bg-white/10' : 'bg-accent')
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </header>
    )
}

export default Header
